import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getLearnerStats, getProgression } from "@/api/learner";
import { getWallet } from "@/api/rewards";
import { num, tierLabel } from "./format";
import { StatsBanner } from "./stats-banner";

/**
 * Compact "Your stats" band for the Train tab. Mirrors the Stats page "Your stats"
 * header and reuses its exact query keys (["progression"], ["learner-stats"]) so the
 * cache and figures are shared with the tab — esp. all-time Answered, which is the
 * same sum-of-topic-attempts the tab header computes.
 */
export function PersonalStatsBanner() {
  const { t } = useTranslation();
  const progression = useQuery({ queryKey: ["progression"], queryFn: getProgression });
  const stats = useQuery({ queryKey: ["learner-stats"], queryFn: getLearnerStats });
  const wallet = useQuery({ queryKey: ["rewards-wallet"], queryFn: getWallet });

  // Non-critical shortcut: hide on error rather than disrupt the Train hero.
  if (progression.isError) return null;

  const p = progression.data;
  const topics = stats.data?.topics ?? [];
  const totalAnswered = topics.reduce((a, t) => a + t.n_attempts, 0);
  const weakCount = stats.data?.weak_topics?.length ?? 0;

  return (
    <StatsBanner
      title={t("Your stats")}
      to="/app/stats?tab=you"
      cta={t("View your stats")}
      loading={progression.isLoading}
      tiles={[
        { label: t("Rank"), value: tierLabel(p?.tier), sub: p ? `${num(p.total_xp)} XP` : undefined },
        {
          label: t("Current streak"),
          value: `${p?.current_streak ?? 0}🔥`,
          sub: p ? t("Best {{count}}", { count: p.longest_streak }) : undefined,
        },
        {
          label: t("Topics tracked"),
          value: num(topics.length),
          sub: t("{{count}} need work", { count: weakCount }),
        },
        {
          label: t("Answered"),
          value: num(totalAnswered),
          sub: t("all-time"),
        },
        {
          label: t("Unlock balance"),
          value: num(wallet.data?.balance_minutes ?? 0),
          sub: t("available across devices"),
        },
        {
          label: t("Lifetime earned"),
          value: num(wallet.data?.lifetime_earned ?? 0),
          sub: t("minutes, all devices"),
        },
      ]}
    />
  );
}
